/**
 * Personal 802.1X credential (#300).
 *
 * Show-once by design: the secret exists only in the create/rotate response and
 * is unrecoverable afterwards, so the UI is explicit about that rather than
 * implying it can be looked up later. The member sets their own secret, and the
 * value is never logged or audited.
 *
 * The whole card is hidden unless the member's building actually has an 802.1X
 * network, so nothing changes for sites that have not enabled it.
 *
 * Lives on the Network page next to the shared Wi-Fi, because that is where a
 * member looks for Wi-Fi — not under account settings.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, tenantApi } from "@/lib/api/client";
import {
  Alert, Box, Card, Badge, Button, Divider, Text, Stack, Group, Table, SegmentedControl,
  PasswordInput, Switch, List,
} from "@mantine/core";
import { Copy } from "lucide-react";
import { toast } from "sonner";

function CopyableValue({ value, mono = true }: { value: string; mono?: boolean }) {
  return (
    <Group gap={6} wrap="nowrap">
      <Text fz="sm" ff={mono ? "monospace" : undefined} style={{ wordBreak: "break-all" }}>
        {value}
      </Text>
      <Button
        size="compact-xs"
        variant="subtle"
        aria-label={`Copy ${value}`}
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success("Copied");
        }}
      >
        <Copy size={13} />
      </Button>
    </Group>
  );
}

type DeviceKind = "mobile" | "computer";
type Platform = "ios" | "android" | "mac" | "windows" | "linux";

/**
 * Where each platform hides the two settings people get wrong.
 *
 * Field NAMES differ per platform and that is the whole difficulty: the same
 * setting is "EAP method" on Android, "Authentication" on Linux and "Choose a
 * network authentication method" on Windows. Naming them per platform is the
 * difference between following this and guessing.
 */
const PLATFORM_HINTS: Record<Platform, { where: string; eapLabel: string; innerLabel: string }> = {
  android: {
    where: "Settings → Wi-Fi → tap the network",
    eapLabel: "EAP method",
    innerLabel: "Phase 2 authentication",
  },
  windows: {
    where: "Settings → Network → Wi-Fi → Manage known networks → Add",
    eapLabel: "Choose a network authentication method → EAP-TTLS",
    innerLabel: "Non-EAP method (inner identity authentication)",
  },
  linux: {
    where: "Network settings → Wi-Fi → the network → Security",
    eapLabel: "Authentication → Tunneled TLS",
    innerLabel: "Inner authentication",
  },
  ios: { where: "", eapLabel: "", innerLabel: "" },
  mac: { where: "", eapLabel: "", innerLabel: "" },
};

/**
 * Manual settings for a device that cannot take the Apple profile.
 *
 * This is not padding. Android defaults to PEAP and answers our EAP-TTLS offer
 * with a NAK, which fails before any credential is checked — so it writes no
 * attempt row and is invisible in the audit log. A member with no instructions
 * sees only "could not connect to network" and has no way to work out why.
 */
function ManualSettings({
  platform, ssid, serverName, identity,
}: { platform: Platform; ssid: string; serverName: string; identity: string }) {
  const hint = PLATFORM_HINTS[platform];

  const rows: [string, string, string?][] = [
    ["Network name (SSID)", ssid],
    [hint.eapLabel || "EAP method", "TTLS", "Not PEAP — most devices offer PEAP first, and it cannot connect here."],
    [hint.innerLabel || "Phase 2 authentication", "PAP"],
    ["CA certificate", "Use system certificates"],
    ...(serverName ? ([["Domain", serverName]] as [string, string][]) : []),
    ["Identity", identity],
    ["Anonymous identity", "leave empty"],
    ["Password", "the Wi-Fi password above"],
  ];

  return (
    <Stack gap="xs">
      {hint.where && (
        <Text fz="sm" c="dimmed">
          Find it under <Text span fw={600}>{hint.where}</Text>, then match these:
        </Text>
      )}
      <Table withTableBorder withRowBorders fz="sm">
        <Table.Tbody>
          {rows.map(([label, value, note]) => (
            <Table.Tr key={label}>
              <Table.Td w="45%">
                <Text fz="sm">{label}</Text>
                {note && <Text fz="xs" c="dimmed">{note}</Text>}
              </Table.Td>
              <Table.Td>
                <CopyableValue
                  value={value}
                  mono={label !== "Anonymous identity" && label !== "Password" && label !== "CA certificate"}
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {serverName && (
        <Alert color="yellow" variant="light" p="xs">
          <Text fz="xs">
            Your device will ask you to trust a server called{" "}
            <Text span ff="monospace" fz="xs">{serverName}</Text>. That is the right one.
            If it shows any other name, stop and tell us — do not enter your password.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Two-step device picker, because the right answer differs completely: Apple
 * devices take a profile and need no settings at all, everything else needs
 * eight fields typed by hand.
 */
type ApplePlan = "standalone" | "mdm";

function DeviceSetup({
  ssid, serverName, identity, onDownloadProfile, downloading,
}: {
  ssid: string;
  serverName: string;
  identity: string;
  onDownloadProfile: (bulk: boolean) => void;
  downloading: boolean;
}) {
  const [kind, setKind] = useState<DeviceKind | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [plan, setPlan] = useState<ApplePlan>("standalone");

  const choose = (k: DeviceKind) => {
    setKind(k);
    setPlatform(null);
  };

  const isApple = platform === "ios" || platform === "mac";

  return (
    <Stack gap="sm">
      <Text fz="sm" fw={500}>Set up a device</Text>

      <SegmentedControl
        value={kind ?? ""}
        onChange={(v) => choose(v as DeviceKind)}
        data={[
          { label: "Phone or tablet", value: "mobile" },
          { label: "Computer", value: "computer" },
        ]}
        fullWidth
      />

      {kind === "mobile" && (
        <SegmentedControl
          value={platform ?? ""}
          onChange={(v) => setPlatform(v as Platform)}
          data={[
            { label: "iPhone or iPad", value: "ios" },
            { label: "Android", value: "android" },
          ]}
          fullWidth
        />
      )}

      {kind === "computer" && (
        <SegmentedControl
          value={platform ?? ""}
          onChange={(v) => setPlatform(v as Platform)}
          data={[
            { label: "Mac", value: "mac" },
            { label: "Windows", value: "windows" },
            { label: "Linux", value: "linux" },
          ]}
          fullWidth
        />
      )}

      {isApple && (
        <Stack gap="xs">
          <SegmentedControl
            size="xs"
            value={plan}
            onChange={(v) => setPlan(v as ApplePlan)}
            data={[
              { label: "Standalone", value: "standalone" },
              { label: "IT / MDM", value: "mdm" },
            ]}
          />

          {plan === "mdm" && (
            <Text fz="xs" c="dimmed">
              For an IT admin pushing this to every device with your own MDM
              tool (Jamf, Intune, Apple Business Manager, …). It carries no
              one person&apos;s sign-in, so each device prompts for both a
              username and a password on its own — nothing to reissue if
              someone leaves.
            </Text>
          )}

          {platform === "ios" ? (
            <Text fz="sm" c="dimmed">
              Download the profile, then open Settings — it appears at the top as
              &quot;Profile Downloaded&quot;. Install it and enter your Wi-Fi{" "}
              {plan === "mdm" ? "username and password" : "password"} when asked.
            </Text>
          ) : (
            <Stack gap={4}>
              <Text fz="sm" c="dimmed">
                On macOS Ventura and later, opening the profile only stages it —
                you still have to approve the install yourself:
              </Text>
              <List size="sm" c="dimmed" type="ordered" spacing={2}>
                <List.Item>Download the profile below and open it.</List.Item>
                <List.Item>
                  Open <Text span fw={600}>System Settings</Text>, then{" "}
                  <Text span fw={600}>General → Device Management</Text>{" "}
                  (older macOS: <Text span fw={600}>Profiles</Text>).
                </List.Item>
                <List.Item>
                  Under &quot;Downloaded&quot;, double-click the profile, click{" "}
                  <Text span fw={600}>Install...</Text>, and enter your Mac
                  administrator password when asked.
                </List.Item>
                <List.Item>
                  Enter your Wi-Fi {plan === "mdm" ? "username and password" : "password"} when asked.
                </List.Item>
              </List>
              <Text fz="xs" c="dimmed">
                It&apos;s done once the profile moves from &quot;Downloaded&quot; to your
                active profiles list.
              </Text>
              <Alert color="yellow" variant="light" p="xs">
                <Text fz="xs">
                  macOS discards a staged profile if it isn&apos;t approved within 8
                  minutes. If it&apos;s missing from Device Management, just open the
                  downloaded <Text span ff="monospace" fz="xs">.mobileconfig</Text> file
                  again to restart the prompt.
                </Text>
              </Alert>
            </Stack>
          )}
          <Button
            size="sm"
            onClick={() => onDownloadProfile(plan === "mdm")}
            disabled={downloading}
            w="fit-content"
          >
            {downloading ? "Preparing..." : plan === "mdm" ? "Download MDM profile" : "Download setup profile"}
          </Button>
          <Text fz="xs" c="dimmed">
            {plan === "mdm"
              ? "The profile carries the network name and sign-in method, but no password and no one person's identity — everything is typed on the device."
              : "The profile carries the network name and sign-in method, but never your password — you type that once, on the device."}
          </Text>
        </Stack>
      )}

      {platform && !isApple && (
        <ManualSettings
          platform={platform}
          ssid={ssid}
          serverName={serverName}
          identity={identity}
        />
      )}
    </Stack>
  );
}

/** Mirrors ``dot1x.models.MIN_CHOSEN_SECRET_LENGTH`` — the backend is the
 *  authority; this only keeps the button from posting an obvious reject. */
const MIN_SECRET_LENGTH = 12;

/** django-ninja puts the reason in `detail`. */
function errorDetail(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === "string" ? detail : null;
}

/** The member's personal 802.1X sign-in. `onCreated` fires after a new
 *  password is issued (the Get online guide re-checks its step, #436). */
export function Dot1xCredentialCard({ onCreated }: { onCreated?: () => void } = {}) {
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState<string | null>(null);

  const { data: status } = useQuery({
    queryKey: ["dot1x-status"],
    queryFn: () => tenantApi.getDot1xStatus().then((r) => r.data),
    retry: false,
  });

  // Choosing your own is opt-in: the generated 20-character secret stays the
  // default because it is stronger, but typing it into a phone, a laptop and a
  // printer is the friction members actually complain about.
  const [chooseOwn, setChooseOwn] = useState(false);
  const [chosen, setChosen] = useState("");

  const issue = useMutation({
    mutationFn: (secret?: string) => tenantApi.createDot1xCredential(secret).then((r) => r.data),
    onSuccess: (data) => {
      setRevealed(data.secret);
      setChooseOwn(false);
      setChosen("");
      qc.invalidateQueries({ queryKey: ["dot1x-status"] });
      onCreated?.();
    },
    // The backend's message names the actual rule broken (too short, reused
    // account password, ...), so show it rather than a generic failure.
    onError: (error) =>
      toast.error(errorDetail(error) || "Could not create your Wi-Fi password"),
  });

  const revoke = useMutation({
    mutationFn: () => tenantApi.revokeDot1xCredential().then((r) => r.data),
    onSuccess: () => {
      setRevealed(null);
      toast.success("Wi-Fi password revoked");
      qc.invalidateQueries({ queryKey: ["dot1x-status"] });
    },
    onError: () => toast.error("Could not revoke your Wi-Fi password"),
  });

  // Fetched as a blob rather than linked with <a href>: the endpoint needs the
  // Bearer token, which a plain navigation would not send (it would 401).
  //
  // `bulk` drops the requesting member's own identity from the profile —
  // that variant is for an IT admin to push via their own MDM to every
  // device, and it must not carry one specific person's email. Each device
  // ends up prompting for both username and password instead of just the
  // password the standalone profile leaves out.
  const profile = useMutation({
    mutationFn: async (bulk: boolean) => {
      const response = await api.get("/dot1x/me/mobileconfig/", {
        params: bulk ? { bulk: true } : undefined,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${status?.ssid || "wifi"}${bulk ? "-mdm" : ""}.mobileconfig`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error("Could not download the setup profile"),
  });

  if (!status?.configured) return null;

  const active = status.has_credential && status.is_active;

  return (
    <Card padding="lg" withBorder>
      <Stack gap="md">
        <Box>
          <Text fw={600} fz="lg">Personal Wi-Fi</Text>
          <Text fz="sm" c="dimmed">
            Sign in to {status.ssid} with your own email and password, on every device.
          </Text>
        </Box>

        <Stack gap={4}>
          <Text fz="sm" c="dimmed">Network Name (SSID)</Text>
          <Badge variant="light" size="lg" px={12}>{status.ssid}</Badge>
        </Stack>

        {revealed ? (
          <Alert color="green" variant="light" p="sm">
            <Stack gap={6}>
              <Text fz="sm" fw={500}>Your Wi-Fi password</Text>
              <CopyableValue value={revealed} />
              <Text fz="xs" c="dimmed">
                Copy it now — for your security it cannot be shown again. If you lose
                it, create a new one.
              </Text>
            </Stack>
          </Alert>
        ) : (
          <Text fz="sm" c="dimmed">
            {active
              ? "Your Wi-Fi password is active. It is stored encrypted, so it can't be shown again — create a new one if you've lost it."
              : "You don't have a Wi-Fi password yet."}
          </Text>
        )}

        {active && status.last_used_at && (
          <Text fz="xs" c="dimmed">
            Last used {new Date(status.last_used_at).toLocaleString()}
            {status.last_used_nas ? ` · ${status.last_used_nas}` : ""}
          </Text>
        )}

        <Switch
          size="sm"
          checked={chooseOwn}
          onChange={(e) => setChooseOwn(e.currentTarget.checked)}
          label="Choose my own password"
          description="Otherwise we generate a strong one for you."
        />

        {chooseOwn && (
          <PasswordInput
            size="sm"
            label="Your Wi-Fi password"
            description={`At least ${MIN_SECRET_LENGTH} characters, and not your Nodus password.`}
            placeholder="Something you can type on every device"
            value={chosen}
            onChange={(e) => setChosen(e.currentTarget.value)}
          />
        )}

        <Group gap="sm">
          <Button
            size="sm"
            onClick={() => issue.mutate(chooseOwn ? chosen : undefined)}
            disabled={issue.isPending || (chooseOwn && chosen.trim().length < MIN_SECRET_LENGTH)}
          >
            {issue.isPending ? "Working..." : active ? "Create new password" : "Create Wi-Fi password"}
          </Button>
          {active && (
            <Button
              size="sm"
              variant="light"
              color="red"
              onClick={() => revoke.mutate()}
              disabled={revoke.isPending}
            >
              {revoke.isPending ? "Working..." : "Revoke"}
            </Button>
          )}
        </Group>

        {active && (
          <Text fz="xs" c="dimmed">
            Creating a new password immediately stops the old one working, so you'll
            need to sign in again on each device.
          </Text>
        )}

        <Divider />
        <DeviceSetup
          ssid={status.ssid || ""}
          serverName={status.server_name}
          identity={status.identity}
          onDownloadProfile={(bulk) => profile.mutate(bulk)}
          downloading={profile.isPending}
        />
      </Stack>
    </Card>
  );
}

export default Dot1xCredentialCard;
